import type { EnhancedGamepad } from "./types";

/**
 * Apply deadzone to axis value
 */
export function applyDeadzone(value: number, deadzone: number): number {
  return Math.abs(value) < deadzone ? 0 : value;
}

/**
 * Check if gamepad has changed
 */
export function hasGamepadChanged(
  current: EnhancedGamepad | undefined,
  previous: EnhancedGamepad | undefined,
): boolean {
  if (!current && !previous) return false;
  if (!current || !previous) return true;

  const currentGp = current.gamepad;
  const previousGp = previous.gamepad;

  // Check basic properties
  if (currentGp.connected !== previousGp.connected) return true;
  if (currentGp.timestamp !== previousGp.timestamp) return true;

  // Check buttons
  if (currentGp.buttons.length !== previousGp.buttons.length) return true;
  for (let i = 0; i < currentGp.buttons.length; i++) {
    if (currentGp.buttons[i].value !== previousGp.buttons[i].value) return true;
  }

  // Check axes
  if (currentGp.axes.length !== previousGp.axes.length) return true;
  for (let i = 0; i < currentGp.axes.length; i++) {
    if (currentGp.axes[i] !== previousGp.axes[i]) return true;
  }

  return false;
}

/**
 * Detect gamepad type based on gamepad ID string (same logic as getTypeGamepad)
 */
export function getTypeGamepad(
  id: string,
): "xbox" | "nintendo" | "sony" | "generic" {
  if (!id || typeof id !== "string") return "generic";

  // Detect specific gamepad models first (before vendor ID checks)
  const gamepadPatterns = {
    sony: /dualshock|dualsense|playstation|ps[3-5]/i,
    xbox: /xbox|x-box|xinput|standard gamepad/i,
    nintendo: /nintendo|joy-con|pro controller|switch/i,
  };

  if (gamepadPatterns.sony.test(id)) return "sony";
  if (gamepadPatterns.xbox.test(id)) return "xbox";
  if (gamepadPatterns.nintendo.test(id)) return "nintendo";

  // Remote/TV patterns - check before vendor ID fallback
  if (/remote|bravia|samsung|tizen|webos|lg gamepad/i.test(id)) {
    return "generic";
  }

  // Vendor ID based detection - only if no specific model name matched
  // 054c = Sony, but could be TV remote or gamepad
  // Check for gamepad-specific product IDs under Sony vendor
  if (/054c/i.test(id)) {
    // Known DualShock/DualSense product IDs
    const sonyGamepadProducts = /0268|05c4|09cc|0ba0|0ce6|0df2|0e5f|0e20/i;
    if (sonyGamepadProducts.test(id)) return "sony";
    // If it has 054c but no known gamepad product ID, it's likely a TV remote
    return "generic";
  }
  if (/057e/i.test(id)) return "nintendo";

  // SHANWAN vendor ID (2563) - Xbox-compatible controllers
  if (/2563/i.test(id)) return "xbox";

  // DualShock 4 often reports as "Wireless Controller" without vendor ID on some TVs
  if (/^wireless controller$/i.test(id.trim())) return "sony";

  // Onmo wireless controller
  if (/onmo/i.test(id.trim())) return "xbox";

  // EvoFox controllers (report as xinput-compatible)
  if (/evofox/i.test(id.trim())) return "xbox";

  // SHANWAN controllers (Xbox-compatible Android gamepads)
  if (/shanwan/i.test(id.trim())) return "xbox";

  return "generic";
}

/**
 * Detect if gamepad is an actual game controller vs TV remote
 */
export function detectDeviceType(
  gamepadId: string,
): "xbox" | "nintendo" | "sony" | "remote" {
  const type = getTypeGamepad(gamepadId);
  return type !== "generic" ? type : "remote";
}

/**
 * Check if gamepad is an actual gaming controller (not a TV remote)
 */
export function isActualGameController(gamepadId: string): boolean {
  return detectDeviceType(gamepadId) !== "remote";
}

/**
 * Create enhanced gamepad from browser Gamepad object
 */
export function createEnhancedGamepad(
  gamepad: Gamepad,
  sequentialIndex: number,
  mappedIndex: number,
  deadzone = 0.05,
  knownDeviceType?: "xbox" | "nintendo" | "sony" | "remote",
): EnhancedGamepad {
  const deviceType = knownDeviceType ?? detectDeviceType(gamepad.id);

  const enhancedGamepad = {
    id: gamepad.id,
    index: gamepad.index,
    connected: gamepad.connected,
    timestamp: gamepad.timestamp,
    mapping: gamepad.mapping,
    axes: Array.from(gamepad.axes, (axis) => applyDeadzone(axis, deadzone)),
    buttons: Array.from(gamepad.buttons, (btn) => ({
      pressed: btn.pressed,
      touched: btn.touched,
      value: btn.value,
    })),
    hapticActuators:
      "hapticActuators" in gamepad ? gamepad.hapticActuators : undefined,
    vibrationActuator:
      "vibrationActuator" in gamepad ? gamepad.vibrationActuator : undefined,
  } as unknown as Gamepad;

  return {
    gamepad: enhancedGamepad,
    sequentialIndex,
    mappedIndex,
    deviceType,
  };
}

/**
 * Get connected gamepads from navigator
 */
export function getConnectedGamepads(): (Gamepad | null)[] {
  try {
    const list = navigator.getGamepads();
    return [list[0] ?? null, list[1] ?? null, list[2] ?? null, list[3] ?? null];
  } catch (error) {
    console.warn("[GamepadManager] Error getting gamepads:", error);
    return [];
  }
}

/**
 * Generate unique listener ID
 */
export function generateListenerId(prefix = "listener"): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Convert enhanced gamepads to browser gamepad array format
 */
export function enhancedGamepadsToArray(
  enhanced: EnhancedGamepad[],
): (Gamepad | null)[] {
  const result: (Gamepad | null)[] = [null, null, null, null];

  enhanced.forEach((item) => {
    const browserIndex = item.gamepad.index;
    if (browserIndex < 4) {
      result[browserIndex] = item.gamepad;
    }
  });

  return result;
}

/**
 * Debounce function for performance optimization
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}
